import { useState, useEffect } from "react";
import combinedServices from "../../../../shared/services/user-service";
import Accordion from "react-bootstrap/Accordion";
import categoryIcon from "../../../../assets/images/cat_Icon.png";
import moment from "moment";
import Swal from "sweetalert2";
import jsPDF from 'jspdf';

const OrderComponent = ({
  storeID,
  storeName,
  page,
  limit,
  searchQuery,
  setPage,
  setLimit,
  setSearchQuery
}) => {
  const [orderData, setOrderData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [updatedOrderStatus, setUpdatedOrderStatus] = useState("");
  const [refundedData, setRefundedData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentNote, setCurrentNote] = useState("");
  const [currentUserID, setCurrentUserID] = useState(null);
  const [currentEventID, setCurrentEventID] = useState(null);

  const totalPages = Math.ceil(totalOrders / limit);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const orderResponse = await combinedServices.getallorders(
          page,
          limit,
          searchQuery,
          paymentStatus,
          orderStatus,
          storeID
        );
        if (orderResponse?.data?.data) {
          setOrderData(orderResponse.data.data || []);
          setTotalOrders(orderResponse.data.total || 0);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };
    fetchOrders();
  }, [
    page,
    limit,
    searchQuery,
    paymentStatus,
    orderStatus,
    updatedOrderStatus,
    refundedData,
    refreshTrigger,
    storeID
  ]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handlePaymentStatusChange = (e) => {
    setPaymentStatus(e.target.value);
    setPage(1);
  };

  const handleOrderStatusChange = (e) => {
    setOrderStatus(e.target.value);
    setPage(1);
  };

  const groupedOrders = orderData.reduce((acc, order) => {
    if (!acc[order.order_id]) acc[order.order_id] = [];
    acc[order.order_id].push(order);
    return acc;
  }, {});

  const sortedGroupKeys = Object.keys(groupedOrders).sort(
    (a, b) =>
      new Date(groupedOrders[b][0].orderDate) -
      new Date(groupedOrders[a][0].orderDate)
  );

  const formatDate = (dateString) => {
    return moment(dateString).format("MM/DD/YYYY HH:mm:ss");
  };

  const updateOrderstatus = async (
    id,
    status,
    inv_id,
    userId,
    product_id,
    attribute,
    product
  ) => {
    try {
      let confirmText = `You are about to update the order status to "${status}".`;
      
      // Check if status is Cancelled and attributes start with Classes or Transponders
      if (status === "Cancelled") {
        const parsedAttribute = JSON.parse(attribute);
        const hasClassesOrTransponders = Object.keys(parsedAttribute).some(
          key => key.startsWith("Classes") || key.startsWith("Transponders")
        );
        
        if (hasClassesOrTransponders) {
          confirmText += "\n\nNote: All free and mandatory products will also be cancelled.";
        }
      }

      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: confirmText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, update it!",
        cancelButtonText: "No, cancel"
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      Swal.fire({
        title: "Updating...",
        text: "Please wait while we update the order status.",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        timerProgressBar: true
      });

      const statusData = { id, status };
      const orderUpdateResponse = await combinedServices.updateOrderstatus(
        statusData
      );
      if (status === "Cancelled") {
        const parsedAttribute = JSON.parse(attribute);
        const hasClassesOrTransponders = Object.keys(parsedAttribute).some(
          key => key.startsWith("Classes") || key.startsWith("Transponders")
        );
        
        if (
          (parsedAttribute.Classes || parsedAttribute.Transponders) &&
          inv_id === 0 &&
          product === null
        ) {
          const cancelUserEventEntryResponse =
            await combinedServices.cancelUserEventEntry({
              id: id,
              userID: userId,
              eventId: product_id
            });
          setRefreshTrigger((prev) => prev + 1);
        }
        
        // Cancel all 0 unit price products for this order when hasClassesOrTransponders is true
        if (hasClassesOrTransponders) {
          const currentOrderProducts = groupedOrders[Object.keys(groupedOrders).find(orderId => 
            groupedOrders[orderId].some(order => order.id === id)
          )] || [];
          
          const zeroUnitPriceProducts = currentOrderProducts.filter(order => 
            parseFloat(order.unit_price || 0) === 0 && order.id !== id
          );
          
          for (const zeroProduct of zeroUnitPriceProducts) {
            try {
              await combinedServices.updateOrderstatus({
                id: zeroProduct.id,
                status: "Cancelled"
              });
            } catch (error) {
              console.error(`Error cancelling zero price product ${zeroProduct.id}:`, error);
            }
          }
        }
      }
      if (orderUpdateResponse?.data) {
        setUpdatedOrderStatus(orderUpdateResponse.data || []);
        
        Swal.fire({
          title: "Success!",
          text: orderUpdateResponse.data.message,
          icon: "success"
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to update the order status. Please try again.",
          icon: "error"
        });
      }
    } catch (error) {
      console.error("Error Updating orders:", error);

      Swal.fire({
        title: "Error!",
        text: "An error occurred while updating the order status.",
        icon: "error"
      });
    }
  };

  const getStatusClass = (status, type) => {
    if (type === "payment") {
      return (
        {
          COMPLETED: "status-completed-delivered",
          PENDING: "status-pending",
          FAILED: "status-failed-cancelled"
        }[status] || "status-default"
      );
    }
    if (type === "order") {
      return (
        {
          Delivered: "status-completed-delivered",
          Pending: "status-pending",
          Cancelled: "status-failed-cancelled"
        }[status] || "status-default"
      );
    }
  };

  const cancelOrder = async (id, payment_id, price, inv_id, storeid) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to cancel this order?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it!",
        cancelButtonText: "No, keep it"
      });

      if (result.isConfirmed) {
        setIsLoading(true);

        const data = {
          id: id,
          payment_id: payment_id,
          refundableAmount: price,
          inv_id: inv_id,
          storeid: storeid
        };

        const CancelOrderResponse = await combinedServices.CancelOrder(data);

        if (CancelOrderResponse.data.status === 200) {
          setRefundedData("REFUNDED");
          Swal.fire(
            "Cancelled!",
            "Your order has been successfully cancelled.",
            "success"
          );
          setIsLoading(false);
        } else {
          Swal.fire("Failed!", "Failed to cancel the order.", "error");
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      Swal.fire(
        "Error!",
        "An error occurred while cancelling the order.",
        "error"
      );
      setIsLoading(false);
    }
  };

  const generateOrderPDF = async (orders) => {
    const ordersGrouped = orders.reduce((acc, order) => {
      if (!acc[order.order_id]) {
        acc[order.order_id] = [];
      }
      acc[order.order_id].push(order);
      return acc;
    }, {});

    const DEFAULT_IMAGE = 'https://nambaweb.s3.ap-south-1.amazonaws.com/frontend-static-image/logo.png';
    let backgroundImageBase64 = null;

    try {
      const relevantItems = orders.filter((item) => {
        try {
          const attributes = JSON.parse(item.attribute || "{}");
          return Object.keys(attributes).some(
            (key) => key.startsWith("Classes") || key.startsWith("Transponders")
          );
        } catch {
          return false;
        }
      });

      if (relevantItems.length > 0) {
        for (const item of relevantItems) {
          try {
            let imageUrl = DEFAULT_IMAGE;
            if (item.image) {
              const parsedImages = JSON.parse(item.image);
              if (Array.isArray(parsedImages) && parsedImages.length > 0) {
                imageUrl = parsedImages[0];
              }
            }
            const body = { url: imageUrl };
            const urltobase64 = await combinedServices.UrlToBase64(body);
            if (urltobase64?.data?.base64img) {
              // Clean the base64 string - remove any existing data URL prefix
              let cleanBase64 = urltobase64.data.base64img;
              if (cleanBase64.startsWith('data:')) {
                cleanBase64 = cleanBase64.split(',')[1];
              }
              backgroundImageBase64 = cleanBase64;
              break;
            }
          } catch (error) {
            console.error('Error processing item image:', error);
          }
        }
      }

      if (!backgroundImageBase64) {
        const body = { url: DEFAULT_IMAGE };
        const urltobase64 = await combinedServices.UrlToBase64(body);
        if (urltobase64?.data?.base64img) {
          let cleanBase64 = urltobase64.data.base64img;
          if (cleanBase64.startsWith('data:')) {
            cleanBase64 = cleanBase64.split(',')[1];
          }
          backgroundImageBase64 = cleanBase64;
        }
      }
    } catch (error) {
      console.error('Error processing background image:', error);
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    let isFirstPage = true;

    Object.entries(ordersGrouped).forEach(([orderId, orderItems], pageIndex) => {
      if (!isFirstPage) {
        pdf.addPage();
      }
      isFirstPage = false;

      const firstItem = orderItems[0];
      const primaryMember = orderItems.find(item => item.userDetails?.user_type === "member_adult_primary");
      const personal = primaryMember?.userDetails || orderItems[0].userDetails;
      const primaryMemberOrder = primaryMember || firstItem;
      
      // Header
      pdf.setFont('arial', 'bold');
      pdf.setFontSize(22);
      pdf.text(storeName || "Store", 105, 25, { align: 'center' });
      
      pdf.setFont('arial', 'normal');
      pdf.setFontSize(18);
      pdf.text('Order Report', 105, 35, { align: 'center' });

      // Customer Info Section
      let yPos = 50;
      pdf.setFont('arial', 'bold');
      pdf.setFontSize(13);
      pdf.text(String(primaryMemberOrder?.username || "N/A"), 20, yPos);
      
      yPos += 6;
      pdf.setFontSize(12);
      
      // Customer details grid
      const customerDetails = [
        ['User ID', String(primaryMemberOrder?.userId || "N/A")],
        ['Email', String(personal?.user_email || "N/A")],
        ['Home Phone', String(personal?.user_phone || "N/A")],
        ['Mobile Phone', String(personal?.user_mobile || "N/A")]
      ];
      
      pdf.setFontSize(10);
      customerDetails.forEach(([label, value], index) => {
        const xPos = index === 0 ? 20 : 20 + 25 + ((index - 1) * 50);
        pdf.setFont('arial', 'bold');
        pdf.text(String(label), xPos, yPos);
        pdf.setFont('arial', 'normal');
        pdf.text(String(value), xPos, yPos + 5);
      });

      yPos += 12;
      
      // Order details grid
      const orderDetails = [
        ['Order', String(firstItem?.order_id || "N/A")],
        ['Order Date', String(formatDate(firstItem?.orderDate) || "N/A")],
        // ['Order Status', String(firstItem?.orderStatus || "N/A")],
        ['Payment Status', String(firstItem?.paymentStatus || "N/A")],
        ['Transaction ID', String(firstItem?.transaction_id || "N/A")]
      ];
      
      pdf.setFontSize(9);
      orderDetails.forEach(([label, value], index) => {
        const xPos = 20 + (index * 36);
        pdf.setFont('arial', 'bold');
        pdf.text(String(label), xPos, yPos);
        pdf.setFont('arial', 'normal');
        pdf.text(String(value), xPos, yPos + 5);
      });

      yPos += 12;

      // Manual table creation
      const tableHeaders = ['Item', 'Qty', 'Price', 'Payment Status', 'Extended Price'];
      const colWidths = [58, 20, 25, 35, 27];
      const colPositions = [20, 78, 98, 123, 158];
      const rowHeight = 8;
      
      // Table header
      pdf.setFillColor(230, 230, 230);
      pdf.rect(20, yPos, 170, rowHeight, 'F');
      pdf.setDrawColor(0, 0, 0);
      pdf.rect(20, yPos, 170, rowHeight);
      
      // Draw column separators for header
      for (let i = 1; i < colPositions.length; i++) {
        pdf.line(colPositions[i], yPos, colPositions[i], yPos + rowHeight);
      }
      
      pdf.setFont('arial', 'bold');
      pdf.setFontSize(12);
      tableHeaders.forEach((header, index) => {
        if (index === 3) { // Payment Status column
          pdf.text(String(header), colPositions[index] + (colWidths[index] / 2), yPos + 5, { align: 'center' });
        } else {
          pdf.text(String(header), colPositions[index] + 2, yPos + 5);
        }
      });
      
      yPos += rowHeight;
      
      // Table rows with pagination
      for (let i = 0; i < orderItems.length; i += 11) {
        const pageItems = orderItems.slice(i, i + 11);
        
        if (i > 0) {
          pdf.addPage();
          yPos = 20;
          
          // Redraw header on new page
          pdf.setFillColor(230, 230, 230);
          pdf.rect(20, yPos, 170, rowHeight, 'F');
          pdf.setDrawColor(0, 0, 0);
          pdf.rect(20, yPos, 170, rowHeight);
          
          for (let j = 1; j < colPositions.length; j++) {
            pdf.line(colPositions[j], yPos, colPositions[j], yPos + rowHeight);
          }
          
          pdf.setFont('arial', 'bold');
          pdf.setFontSize(12);
          tableHeaders.forEach((header, index) => {
            if (index === 3) { // Payment Status column
              pdf.text(String(header), colPositions[index] + (colWidths[index] / 2), yPos + 5, { align: 'center' });
            } else {
              pdf.text(String(header), colPositions[index] + 2, yPos + 5);
            }
          });
          
          yPos += rowHeight;
        }
        
        pageItems.forEach((item, rowIndex) => {
          const attributes = JSON.parse(item.attribute || "{}");
          const attributeText = Object.entries(attributes)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
            .join("; ");
          
          const extendedPrice = item.orderStatus === "Cancelled" ? "0.00" : (Number(item.quantity) * Number(item.unit_price))?.toFixed(2) || "0.00";
          
          const itemName = String(item.product_name || "N/A");
          const currentRowHeight = attributeText ? rowHeight * 2 : rowHeight;
          
          // Draw row background (alternating)
          if (rowIndex % 2 === 1) {
            pdf.setFillColor(248, 248, 248);
            pdf.rect(20, yPos, 170, currentRowHeight, 'F');
          }
          
          // Draw row border
          pdf.setDrawColor(0, 0, 0);
          pdf.rect(20, yPos, 170, currentRowHeight);
          
          // Draw column separators
          for (let j = 1; j < colPositions.length; j++) {
            pdf.line(colPositions[j], yPos, colPositions[j], yPos + currentRowHeight);
          }
          
          pdf.setFont('arial', 'normal');
          pdf.setFontSize(12);
          
          // Item column with attributes
          pdf.text(itemName, colPositions[0] + 2, yPos + 5);
          if (attributeText) {
            pdf.setFontSize(10);
            pdf.setFont('arial', 'italic');
            const maxWidth = colWidths[0] - 4;
            const lines = pdf.splitTextToSize(attributeText, maxWidth);
            pdf.text(lines[0] || '', colPositions[0] + 2, yPos + 12);
            pdf.setFont('arial', 'normal');
            pdf.setFontSize(12);
          }
          
          // Other columns
          pdf.text(String(item.quantity || "0"), colPositions[1] + 8, yPos + 5, { align: 'center' });
          pdf.text(`$${String(item.unit_price || "0.00")}`, colPositions[2] + colWidths[2] - 2, yPos + 5, { align: 'right' });
          // Payment Status with smaller font
          pdf.setFontSize(10);
          if (item.orderStatus === "Cancelled") {
            pdf.text("PRODUCT", colPositions[3] + (colWidths[3] / 2), yPos + 5, { align: 'center' });
            pdf.text("CANCELLED", colPositions[3] + (colWidths[3] / 2), yPos + 11, { align: 'center' });
          } else {
            pdf.text(String(item.paymentStatus || "N/A").toUpperCase(), colPositions[3] + (colWidths[3] / 2), yPos + 10, { align: 'center' });
          }
          pdf.setFontSize(12);
          pdf.text(`$${extendedPrice}`, colPositions[4] + colWidths[4] - 2, yPos + 5, { align: 'right' });
          
          yPos += currentRowHeight;
        });
      }

      // Calculate total
      const total = orderItems
        .reduce((sum, item) => sum + (item.orderStatus === "Cancelled" ? 0 : (Number(item.quantity) || 0) * (Number(item.unit_price) || 0)), 0)
        .toFixed(2);

      // Add total line
      yPos += 10;
      pdf.setFont('arial', 'bold');
      pdf.setFontSize(14);
      pdf.text(`$${total} Total`, 185, yPos, { align: 'right' });
      
      // Add watermark background image on top of everything
      if (backgroundImageBase64) {
        try {
          pdf.saveGraphicsState();
          pdf.setGState(pdf.GState({opacity: 0.2}));
          pdf.addImage(
            backgroundImageBase64,
            'JPEG',
            60, 100, 90, 60
          );
          pdf.restoreGraphicsState();
        } catch (error) {
          console.error('Error adding watermark:', error);
        }
      }
    });

    // Save the PDF
    pdf.save(`${storeName || 'Store'} Report ${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const addDates = async (fromDate, toDate) => {
    try {
      const { value: formValues } = await Swal.fire({
        title: "Enter Date Range",
        html: `<div style="display: flex; justify-content: center; gap: 20px; width: 100%; padding: 0 20px;">
              <div style="flex: 1; display: flex; flex-direction: column; align-items: center;">
                <label for="swal-input1" style="margin-bottom: -10px; font-weight: bold;">From Date</label>
                <input id="swal-input1" class="swal2-input" placeholder="From Date" type="date" style="width: 100%; max-width: 200px;" ${fromDate ? `value="${fromDate}"` : ""
          }>
              </div>
              <div style="flex: 1; display: flex; flex-direction: column; align-items: center;">
                <label for="swal-input2" style="margin-bottom: -10px; font-weight: bold;">To Date</label>
                <input id="swal-input2" class="swal2-input" placeholder="To Date" type="date" style="width: 100%; max-width: 200px;" ${toDate ? `value="${toDate}"` : ""
          }>
              </div>
            </div>`,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Generate Report",
        width: "700px",
        padding: "2em",
        backdrop: true,
        customClass: {
          container: "custom-swal-container",
          popup: "custom-swal-popup",
          input: "custom-swal-input"
        },
        preConfirm: () => {
          return [
            document.getElementById("swal-input1").value,
            document.getElementById("swal-input2").value
          ];
        }
      });

      if (formValues) {
        const [fromDate, toDate] = formValues;
        if (!fromDate || !toDate) {
          Swal.fire("Error!", "Please enter both From and To dates.", "error");
          return;
        }
        const fromDateUTC = new Date(`${fromDate}T00:00:00Z`).toISOString();
        const toDateUTC = new Date(`${toDate}T23:59:59Z`).toISOString();

        setIsLoading(true);
        const response = await combinedServices.orderData({
          fromDate: fromDateUTC,
          toDate: toDateUTC,
          storeID: storeID,
          limit: limit,
          paymentStatus: paymentStatus,
          orderStatus: orderStatus
        })

        if (response?.data?.status === 200 || response?.data?.success) {
          const orderData = response.data.orderData || response.data.data || [];
          if (orderData.length === 0) {
            Swal.fire("Info!", "No orders found for the selected date range.", "info");
            setIsLoading(false);
            return;
          }
          await generateOrderPDF(orderData);
          Swal.fire(
            "Success!",
            response.data.message || "Report generated successfully.",
            "success"
          );
        } else {
          Swal.fire(
            "Warning!",
            response?.data?.message || "An unexpected response was received while generating the report.",
            "warning"
          );
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error generating report:", error);
      setIsLoading(false);
      Swal.fire("Error!", error.message || "Failed to generate the report.", "error");
    }
  };

  const handleOpenNoteModal = (note, userId, eventid) => {
    setCurrentNote(note);
    setCurrentUserID(userId);
    setCurrentEventID(eventid)
    setShowNoteModal(true);
  };

  const handleCloseNoteModal = () => {
    setShowNoteModal(false);
    setCurrentNote("");
    setCurrentEventID("")
    setCurrentUserID(null);
  };

  const handleSaveNote = async () => {
    try {
      if (!currentEventID && !currentUserID) return;

      const response = await combinedServices.changeSpecialNote({
        userID: currentUserID,
        eventID: currentEventID,
        specialNote: currentNote
      });
      if (response.data.status) {
        setRefreshTrigger((prev) => prev + 1);
        Swal.fire("Success!", response.data.message, "success");
      } else {
        throw new Error(response.data.message || "Failed to update note");
      }
    } catch (error) {
      console.error("Error updating special note:", error);
      Swal.fire("Error!", error.message, "error");
    } finally {
      handleCloseNoteModal();
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `Do you really want to delete order ${orderId}? This action cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel",
        confirmButtonColor: "#dc3545"
      });

      if (result.isConfirmed) {
        const response = await combinedServices.deleteOrder({ orderId });
        if (response?.data?.status === 200 || response?.data?.success) {
          setRefreshTrigger((prev) => prev + 1);
          Swal.fire("Deleted!", "Order has been successfully deleted.", "success");
        } else {
          Swal.fire("Error!", response?.data?.message || "Failed to delete order.", "error");
        }
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      Swal.fire("Error!", "An error occurred while deleting the order.", "error");
    }
  };

  return (
    <>
      {isLoading && (
        <div className="loadingOverlay">
          <div className="loadingSpinner"></div>
        </div>
      )}
      
      <div className="storeFilter d-flex justify-content-between align-items-center">
        <form>
          <div className="catFilterCont">
            <div className="filWrp d-flex flex-wrap align-items-center">
              <label className="label2">Showing</label>
              <select
                className="formSelect ms-2 me-2"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span className="ent">entries</span>
              
              <div className="ms-3">
                <label className="label2">Payment Status</label>
                <select
                  className="formSelect ms-2"
                  value={paymentStatus}
                  onChange={handlePaymentStatusChange}
                >
                  <option value="">All Payment</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                  <option value="REFUNDED">Refunded</option>
                </select>
              </div>

              <div className="ms-3">
                <label className="label2">Order Status</label>
                <select
                  className="formSelect ms-2"
                  value={orderStatus}
                  onChange={handleOrderStatusChange}
                >
                  <option value="">All Orders</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div className="srchGrp ms-3">
                <input
                  type="text"
                  className="formInput"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search with Order ID, Product Name or User Name"
                />
              </div>
            </div>
          </div>
        </form>
        <button
          className="nambaBtn"
          onClick={() => addDates(null, null)}
        >
          Download Report
        </button>
      </div>
      &nbsp;
      
      <div className="tblWrp">
        <table>
          <thead>
            <tr>
              <th>User Name</th>
              <th>Product / Event Name</th>
              <th>Description</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Total</th>
              <th>Payment Status</th>
              <th>Order Status</th>
              <th>Special Note</th>
              <th>Transaction ID</th>
              <th>Ordered Date</th>
              <th>Action</th>
            </tr>
          </thead>
        </table>
        
        <Accordion defaultActiveKey="0">
          {sortedGroupKeys.map((orderId, idx) => {
            const ordersWithSameId = groupedOrders[orderId];
            return (
              <Accordion.Item eventKey={String(idx)} key={orderId}>
                <Accordion.Header>
                  <div className="accordion-header-content">
                    <div>
                      <strong>Order ID:</strong> {orderId}{" "}
                      <strong>Username:</strong>{" "}
                      {ordersWithSameId[0]?.username || "N/A"}{" "}
                      <strong>Order Date:</strong>{" "}
                      {formatDate(ordersWithSameId[0]?.orderDate) || "N/A"}{" "}
                      <div
                        style={{
                          display: "flex",
                          gap: "0",
                          justifyContent: "flex-end"
                        }}
                      >
                        <button
                          className="nambaBtn"
                          onClick={(e) => {
                            e.stopPropagation();
                            generateOrderPDF(ordersWithSameId);
                          }}
                          style={{
                            padding: "5px 10px",
                            fontSize: "11px",
                            marginTop: "-20px",
                            borderRadius: "3px 0 0 3px"
                          }}
                        >
                          Download Report
                        </button>
                        {localStorage.getItem("admin_info") && (
                          <button
                            className="nambaBtn"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteOrder(orderId);
                            }}
                            style={{
                              padding: "5px 10px",
                              fontSize: "11px",
                              marginTop: "-20px",
                              borderRadius: "3px 0 0 3px",
                              backgroundColor: "#dc3545",
                              marginLeft: "8px"
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <table>
                    <tbody>
                      {ordersWithSameId.map((order) => {
                        const product = order.product || {};
                        const images = JSON.parse(order.image || "[]");
                        const orderAttributes = JSON.parse(
                          order.attribute || "{}"
                        );
                        const totalPrice = order.quantity * order.unit_price;

                        return (
                          <tr key={order.id}>
                            <td>{order.username}</td>
                            <td>
                              <div className="catPara">
                                <img
                                  src={images[0] || categoryIcon}
                                  alt={order.product_name || "Product Image"}
                                />
                                {order.product_name || "N/A"}
                              </div>
                            </td>
                            <td>
                              {Object.keys(orderAttributes).length > 0 ? (
                                Object.entries(orderAttributes).map(
                                  ([key, value]) => (
                                    <div key={key}>
                                      <strong>{key}:</strong>{" "}
                                      {Array.isArray(value)
                                        ? value.join(", ")
                                        : value}
                                    </div>
                                  )
                                )
                              ) : (
                                <span>N/A</span>
                              )}
                            </td>
                            <td>{order.quantity}</td>
                            <td>${order.unit_price || "N/A"}</td>
                            <td>${totalPrice.toFixed(2)}</td>
                            <td>
                              <span style={{height:"35px",alignContent:"center"}}
                                className={`status-badge ${order.unit_price === "0.00" && order.orderStatus === "Cancelled" 
                                  ? "status-failed-cancelled" 
                                  : getStatusClass(order.paymentStatus, "payment")}`}
                              >
                                {order.unit_price === "0.00" && order.orderStatus === "Cancelled" 
                                  ? "PRODUCT CANCELLED" 
                                  : order.paymentStatus}
                              </span>
                            </td>
                            <td>
                              <div className="orderStatus" >
                                <select style={{height:"35px",alignContent:"center"}}
                                  value={order.orderStatus || "Pending"}
                                  onChange={(e) =>
                                    updateOrderstatus(
                                      order.id,
                                      e.target.value,
                                      order.inv_id,
                                      order.userId,
                                      order.product_id,
                                      order.attribute,
                                      order.product
                                    )
                                  }
                                  className={`form_ctrl ctrlDrop ${getStatusClass(
                                    order.orderStatus,
                                    "order"
                                  )} ${order.paymentStatus !== "COMPLETED" ||
                                    order.orderStatus === "Delivered" ||
                                    order.orderStatus === "Cancelled"
                                    ? "disabled-select"
                                    : ""
                                    }`}
                                  disabled={
                                    order.paymentStatus !== "COMPLETED" ||
                                    order.orderStatus === "Delivered" ||
                                    order.orderStatus === "Cancelled"
                                  }
                                >
                                  <option value="Delivered">DELIVERED</option>
                                  <option value="Pending">PENDING</option>
                                  <option value="Cancelled">CANCELLED</option>
                                </select>
                              </div>
                            </td>
                            <td>
                              <span className='catPara'>
                                {order.special_note || "N/A"}
                              </span>
                              {order.special_note && (
                                <button
                                  onClick={() => handleOpenNoteModal(order.special_note, order.userId, order.product_id)}
                                  className="btn-edit-note"
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#1890ff",
                                    cursor: "pointer",
                                    padding: "0 5px"
                                  }}
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                            <td>{order.transaction_id || "N/A"}</td>
                            <td>{formatDate(order.orderDate) || "N/A"}</td>
                            <td>
                              <span
                                className={`status-badge ${
                                  order.paymentStatus === "COMPLETED" &&
                                  order.orderStatus === "Cancelled" &&
                                  order.unit_price !== "0.00"
                                    ? "status-failed-cancelled"
                                    : "status-default"
                                }`}
                                style={{
                                  height: "35px",
                                  alignContent: "center",
                                  cursor: order.paymentStatus === "COMPLETED" &&
                                         order.orderStatus === "Cancelled" &&
                                         order.unit_price !== "0.00" ? "pointer" : "not-allowed",
                                  opacity: order.paymentStatus === "COMPLETED" &&
                                          order.orderStatus === "Cancelled" &&
                                          order.unit_price !== "0.00" ? 1 : 0.6
                                }}
                                onClick={() => {
                                  if (order.paymentStatus === "COMPLETED" &&
                                      order.orderStatus === "Cancelled" &&
                                      order.unit_price !== "0.00") {
                                    cancelOrder(
                                      order.id,
                                      order.payment_id,
                                      order.price,
                                      order.inv_id,
                                      order.store_id
                                    );
                                  }
                                }}
                              >
                                Initiate Refund
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>

        <div className="tblPagiWrp">
          <ul className="pagination namPagi">
            <li>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Prev
              </button>
            </li>
            {Array.from({ length: totalPages }, (_, index) => (
              <li key={index}>
                <button
                  className={page === index + 1 ? "active" : ""}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}
            <li>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </div>
      </div>

      {showNoteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Special Note</h3>
              <button onClick={handleCloseNoteModal} className="modal-close">
                &times;
              </button>
            </div>
            <div className="modal-body">
              <textarea
                value={currentNote}
                onChange={(e) => setCurrentNote(e.target.value)}
                className="form_ctrl"
                rows={5}
                style={{ width: "100%" }}
              />
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseNoteModal} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleSaveNote} className="btn btn-primary">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderComponent;